import QueryString from 'qs'
import type { UseFetchOptions } from 'nuxt/app'
import type { FetchResponse } from 'ofetch'
import {
  appendServerProfilerLog,
  estimateValueSizeBytes,
  type ProfilerRequestEvent,
} from './profilerLogger'

export function useCustomFetch<T>(params: {
  url: string | (() => string)
  query?: object
  thumbs?: object | null
  options?: UseFetchOptions<T>
  fullResponse?: true
  clientOnly?: boolean
  signal?: AbortSignal
  throw404?: boolean
  recaptchaActionName?: never
  origin?: string | null
}): Promise<FetchResponse<T>>

export function useCustomFetch<T>(params: {
  url: string | (() => string)
  query?: object
  thumbs?: object | null
  options?: UseFetchOptions<T>
  fullResponse?: boolean
  clientOnly: true
  signal?: AbortSignal
  throw404?: boolean
  recaptchaActionName?: string
  origin?: string | null
}): Promise<T>

export function useCustomFetch<T>(params: {
  url: string | (() => string)
  query?: object
  thumbs?: object | null
  options?: UseFetchOptions<T>
  fullResponse?: boolean
  clientOnly?: false
  signal?: AbortSignal
  throw404?: boolean
  recaptchaActionName?: never
  origin?: string | null
}): Promise<ReturnType<typeof useFetch<T>>>

// Jedna implementace odpovídající oběma overloadům
export async function useCustomFetch<T>({
  url,
  query = {},
  thumbs = null,
  options = {},
  fullResponse = false,
  clientOnly = false,
  signal,
  throw404 = true,
  recaptchaActionName,
  origin,
}: {
  url: string | (() => string)
  query?: object
  thumbs?: object | null
  options?: UseFetchOptions<T>
  fullResponse?: boolean
  clientOnly?: boolean
  signal?: AbortSignal
  throw404?: boolean
  recaptchaActionName?: string
  origin?: string | null
}): Promise<T | ReturnType<typeof useFetch<T>> | FetchResponse<T>> {
  const profilerContext = import.meta.server
    ? (() => {
        const nuxtApp = tryUseNuxtApp()
        let profilerEnabled = false
        try {
          profilerEnabled = useState<boolean>('va-profiler-enabled', () => false).value === true
        } catch {
          profilerEnabled = false
        }

        return {
          requestEvent: nuxtApp?.ssrContext?.event as ProfilerRequestEvent | undefined,
          profilerEnabled,
        }
      })()
    : undefined

  const auth = useAuth()
  const { locale, dataLang } = useLocale()
  const { basket } = useBasketService()
  const { public: config } = useRuntimeConfig()
  const { channel } = useChannel()
  const {
    $i18n: { t },
    $sharedComponentsPlugins,
    $sharedComponentsOptions,
  } = useNuxtApp()
  const { addWarningMessage } = useToastService()

  const { recaptchaV2Key, resetReCaptcha, passThrowRecaptchaV3, getRecaptchaToken } = useRecaptcha()

  const objectQuery: { thumb?: object } = { ...query }
  if (thumbs) objectQuery.thumb = thumbs

  const queryString = QueryString.stringify(objectQuery)
  const apiBaseUrl = config.apiBaseUrl ? config.apiBaseUrl : '$NUXT_PUBLIC_API_BASE_URL'

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Lang': locale.value,
    'X-Data-Lang': dataLang.value,
  }

  if (channel.value) {
    headers['X-Channel'] = channel.value
  }
  // Authorization strategy:
  // - JWT mode (default): send Bearer token when available
  // - Sanctum SPA mode (config.sanctumSpa = true): no token cookie, send X-XSRF-TOKEN for
  //   state-changing requests instead
  if (auth.value.token) {
    headers['Authorization'] = `Bearer ${auth.value.token}`
  } else if (
    $sharedComponentsOptions?.options?.auth?.sanctumSpa &&
    options.method &&
    ['POST', 'PUT', 'PATCH', 'DELETE'].includes(String(options.method).toUpperCase())
  ) {
    const xsrfToken = useCookie('XSRF-TOKEN', { readonly: true, decode: decodeURIComponent }).value
    if (xsrfToken) headers['X-XSRF-TOKEN'] = xsrfToken
  }
  if (basket.value.guid) {
    headers['X-Basket-Id'] = basket.value.guid
  }
  if (recaptchaActionName) {
    const { token, versionName } = await getRecaptchaToken(recaptchaActionName)
    headers[versionName] = token
  }
  const fetchUrl = `${origin === null ? '' : (origin ?? apiBaseUrl)}${typeof url === 'function' ? url() : url}${queryString ? `?${queryString}` : ''}`
  let fullRes: FetchResponse<T> | null = null

  const keys: string[] = [fetchUrl, dataLang.value, locale.value]

  if (!$sharedComponentsPlugins || $sharedComponentsPlugins?.eshop === true) {
    const { currency } = useCurrency()
    headers['X-Currency'] = currency.value
    keys.push(currency.value)
  }
  const startedAt = import.meta.server ? Date.now() : 0
  if (clientOnly) {
    try {
      const result = await $fetch(fetchUrl, {
        ...options,
        signal,
        headers: { ...headers, ...options.headers },
        method: options?.method,
        async onResponse({ response }) {
          if (import.meta.server) {
            await appendServerProfilerLog(
              {
                url: fetchUrl,
                method: String(options?.method || 'GET').toUpperCase(),
                status: response.status,
                duration: Math.max(0, Date.now() - startedAt),
                sizeBytes: estimateValueSizeBytes(response._data),
                timestamp: new Date().toISOString(),
              },
              profilerContext
            )
          }
          if (fullResponse) {
            fullRes = response
          }
        },
      })
      if (recaptchaActionName) {
        resetReCaptcha()
      }

      if (fullResponse) {
        return fullRes! as FetchResponse<T>
      }
      return result as T
    } catch (e) {
      if (import.meta.server) {
        await appendServerProfilerLog(
          {
            url: fetchUrl,
            method: String(options?.method || 'GET').toUpperCase(),
            status: Number(e?.response?.status || 0),
            duration: Math.max(0, Date.now() - startedAt),
            sizeBytes: 0,
            timestamp: new Date().toISOString(),
          },
          profilerContext
        )
      }

      if (recaptchaActionName && e?.response?.status === 403) {
        passThrowRecaptchaV3.value = false
        if (recaptchaV2Key.value) {
          recaptchaV2Key.value = undefined
        }
        addWarningMessage(t('Došlo k problému při ověřování. Zkuste to znovu.'))
        return undefined as T
      } else {
        throw e
      }
    }
  }

  const response = await useFetch<T>(fetchUrl, {
    ...options,
    key: keys.join('-'),
    headers: { ...headers, ...options.headers },
    signal,
    onResponse({ response }) {
      if (fullResponse) {
        fullRes = response
      }
    },
    onRequestError(e) {
      console.log(e)
    },
  })
  if (response.error.value) {
    switch (response?.error?.value?.statusCode) {
      case 403: {
        if (recaptchaActionName) {
          passThrowRecaptchaV3.value = false
          if (recaptchaV2Key.value) {
            recaptchaV2Key.value = undefined
          }
        }
      }
    }

    if (
      (throw404 && response?.error?.value?.statusCode === 404) ||
      response.error.value.statusCode !== 404
    ) {
      throw createError({
        statusCode: response.error.value?.statusCode,
        message: response.error.value?.message,
        fatal: true,
      })
    }
  }

  if (import.meta.server) {
    await appendServerProfilerLog(
      {
        url: fetchUrl,
        method: String(options?.method || 'GET').toUpperCase(),
        status: Number(response?.error?.value?.statusCode || 200),
        duration: Math.max(0, Date.now() - startedAt),
        sizeBytes: estimateValueSizeBytes(response?.data?.value),
        timestamp: new Date().toISOString(),
      },
      profilerContext
    )
  }

  if (fullResponse) {
    return fullRes as T
  }

  return response as T
}
